import ChatSettingsConstants from 'settings/constants/ChatSettingsConstants';
import OfficeHoursDay from 'entities/OfficeHoursDay';
import Entity from 'lib/Entity';
import _ from 'lodash';

export type OfficeHoursParsable = { available?: number[] };

/**
 * Returns the local machine's timezone offset, converted for server-side use
 * Note: This WILL lead to issues with DST and changes in location, which isn't a concern right now.
 */
function getTimezoneOffset() {
  return new Date().getTimezoneOffset() * 60 * -1;
}

class OfficeHours extends Entity {
  // TODO: Figure out what to do about this with similar models. Basically, the server doesn't treat
  // this as a record that is created on the client. It initializes it (without an ID) on the server
  isNew() {
    return false;
  }

  parse(response: OfficeHoursParsable) {
    const hours = _.cloneDeep(response);
    if (hours['available']) {
      hours['available'] = hours['available'].map(day => OfficeHoursDay.fromJSON(day));
    }
    return hours;
  }

  defaults() {
    return {
      available: ChatSettingsConstants.OfficeHours.DAYS_DEFAULT.map(day => {
        return OfficeHoursDay.create(day);
      }),
      timezone: getTimezoneOffset(),
    };
  }

  getAvailability(): OfficeHoursDay[] {
    return this.get('available');
  }

  getLabel(): string {
    return this.get('office_hours_label');
  }

  getAvailabilityForToday() {
    const todayDayNumber = new Date().getDay();
    return this.getAvailability().find(
      officeHoursDay => officeHoursDay.getDay() === todayDayNumber
    );
  }

  getNextAvailability() {
    if (this.isAvailableNow()) {
      // XXX function call was getAvailableForToday and never had an error in prod. is the condition ever true?
      console.log('DEBUG OfficeHours isAvailableNow was true');
      return this.getAvailabilityForToday();
    }

    const availabilities = this.getAvailability().reduce<{ [key: number]: OfficeHoursDay }>(
      (map, availability) => {
        map[availability.getDay()] = availability;
        return map;
      },
      {}
    );

    const start = new Date().getDay() + 1;
    for (let i = 0, l = 7, day = start; i < l; day = (start + ++i) % 7) {
      if (day in availabilities) {
        return availabilities[day];
      }
    }
    return null;
  }

  hasAvailability() {
    const availability = this.getAvailability();
    return availability.length > 0;
  }

  isAvailableNow() {
    if (!this.hasAvailability()) {
      return true;
    }

    const availability = this.getAvailabilityForToday();
    if (availability) {
      const now = new Date();
      const currentTime = now.getHours() * 60 * 60 + now.getMinutes() * 60 + now.getSeconds();
      // TODO: Account for timezone?
      return currentTime > availability.getStartTime() && currentTime < availability.getEndTime();
    }
    return false;
  }

  getTimezone() {
    return this.get('timezone');
  }

  // $FlowFixMe missing annotation
  static yieldForCurrentUser(options = {}) {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const userId = CurrentUser.getUserId();
    if (!userId) {
      return Promise.reject(new Error('Undefined userId in yieldForCurrentUser'));
    }

    const hours = new OfficeHours();
    // @ts-ignore
    hours.url = `/users/${userId}/office_hours`;
    return hours.fetch(options);
  }

  // $FlowFixMe missing annotation
  static setForCurrentUser(available: OfficeHoursDay[], options = {}) {
    // XXX remove CurrentUser from entities
    const CurrentUser = require('auth/CurrentUser').default;
    const userId = CurrentUser.getUserId();
    if (!userId) {
      return Promise.reject(new Error('Undefined userId in setForCurrentUser'));
    }

    const hours = new OfficeHours();
    // @ts-ignore
    hours.url = `/users/${userId}/office_hours`;
    return hours.save(
      {},
      Object.assign(options, {
        data: {
          available: available.map(day => day.toJSON()),
          timezone: getTimezoneOffset(),
        },
        patch: true,
      })
    );
  }
}

export default OfficeHours;
